import dayjs from 'dayjs';
import { toIntlLocale } from 'src/functions/date.functions';
import utc from 'dayjs/plugin/utc';
import {
  tCreditPackage,
  tPaymentMethod,
  tPurchaseDraft,
} from 'src/pages/WhatsappApi/WhatsappApi.types.ts';
import { tPaymentMethodOption } from 'src/pages/WhatsappApi/components/modals/PurchaseCreditsModal/PurchaseCreditsModal.types.ts';

// The credits period dates (`period_end`, `expires_at`) arrive from leadbilling
// as a naive wall-clock string in UTC (e.g. "2026-06-23 00:00:00", no offset)
// — the renewal cron floors them to UTC midnight (RET-1473). Some surfaces
// instead send the same instant as ISO-8601 with a `+00:00` offset. `dayjs.utc`
// parses BOTH correctly: a string without an offset is read AS UTC, and one
// with an offset is converted to UTC. We need this plugin for that guarantee.
dayjs.extend(utc);

/**
 * GRO-811 — pure helpers shared across the credits purchase flow.
 *
 * These are domain logic for the credits flow (not owned by any single
 * modal): the resolver/summary/format helpers are consumed by
 * `PurchaseCreditsModal` (Step 1), `ConfirmPurchaseModal` (Step 2) and
 * `PurchaseSuccessModal` (Step 3). They live here alongside
 * `Credits.selectors.ts` so the React component files stay "components only"
 * (`react-refresh/only-export-components`) and the helpers are unit-testable
 * without rendering.
 */

/** 30-day expiration window applied to one-time credits. */
export const CREDITS_EXPIRATION_DAYS = 30;

/**
 * Single source of truth for the credits-flow currency label, shared across
 * all three steps (Step 1 footer/custom input, Step 2 confirm summary, Step 3
 * success receipt) so they can never diverge on the same workspace.
 *
 * Resolution order:
 *   1. `selectedPackage.currency` — a picked preset carries its own currency.
 *   2. `workspaceCurrency` — the authoritative `/credits/packages` currency
 *      (drives the custom-amount path, which has no preset).
 *   3. `packages[0].currency` — catalog fallback while `workspaceCurrency` is
 *      null but presets exist.
 *   4. `'MXN'` — last-resort default (no subscription / Stripe unconfigured).
 *
 * Fixes GRO-811: the custom-amount path previously fell straight to a
 * hardcoded `'MXN'`, mislabelling USD workspaces.
 */
export const resolveCreditCurrency = (params: {
  selectedPackage: tCreditPackage | null;
  workspaceCurrency: string | null;
  packages: tCreditPackage[] | null;
}): string =>
  params.selectedPackage?.currency ??
  params.workspaceCurrency ??
  params.packages?.[0]?.currency ??
  'MXN';

export type tConfirmSummary = {
  credits: number;
  pricePerCreditCents: number;
  totalCents: number;
  currency: string;
  expirationDateLabel: string;
  paymentMethodLast4: string | null;
};

/**
 * Resolve the currently-selected credit package from the draft, or null when
 * the draft uses a custom amount.
 */
export const resolveSelectedPackage = (
  draft: tPurchaseDraft,
  packages: tCreditPackage[] | null,
): tCreditPackage | null => {
  if (!draft.packageId || !packages) return null;
  return packages.find((p) => p.id === draft.packageId) ?? null;
};

/**
 * Sort default-first (matched by exact id) so the modal's existing "first card"
 * auto-select lands on the customer's default, and duplicate cards sharing a
 * last4 still resolve correctly. Returns the list unchanged when there is no
 * default, or it is absent from / already first in the list.
 */
export const sortPaymentMethodsByDefaultFirst = (
  paymentMethods: tPaymentMethodOption[],
  defaultPaymentMethodId: string | null,
): tPaymentMethodOption[] => {
  if (!defaultPaymentMethodId) return paymentMethods;
  const defaultIndex = paymentMethods.findIndex(
    (paymentMethod) => paymentMethod.paymentMethodId === defaultPaymentMethodId,
  );
  if (defaultIndex <= 0) return paymentMethods;
  const reordered = [...paymentMethods];
  const [defaultMethod] = reordered.splice(defaultIndex, 1);
  reordered.unshift(defaultMethod);
  return reordered;
};

/**
 * Resolve the selected payment method from the draft (or fall back to first
 * available — same behavior as Step 1's auto-select).
 */
export const resolveSelectedPaymentMethod = (
  draft: tPurchaseDraft,
  paymentMethods: tPaymentMethod[] | null,
): tPaymentMethod | null => {
  if (!paymentMethods || paymentMethods.length === 0) return null;
  if (!draft.paymentMethodId) return paymentMethods[0];
  return (
    paymentMethods.find((pm) => pm.id === draft.paymentMethodId) ??
    paymentMethods[0]
  );
};

/**
 * Build the summary rendered in the confirm table from the draft + the
 * resolved package and payment method. When the draft uses a custom amount,
 * `pricePerCreditCents` falls back to `unitPriceCents` (Stripe-driven, comes
 * from the `/credits/packages` response). For preset packages we derive
 * `pricePerCreditCents = totalCents / credits` so the row always reflects
 * the BFF-priced reality.
 */
export const buildConfirmSummary = (params: {
  draft: tPurchaseDraft;
  selectedPackage: tCreditPackage | null;
  selectedPaymentMethod: tPaymentMethod | null;
  unitPriceCents: number | null;
  /**
   * Authoritative workspace currency from the `/credits/packages` BFF
   * response. Used as the currency label on the custom-amount path, where
   * there is no `selectedPackage` to carry it — without this the summary
   * fell back to a hardcoded `'MXN'` and mislabelled USD workspaces (GRO-811).
   */
  workspaceCurrency: string | null;
  /** Catalog, for the currency fallback when `workspaceCurrency` is null. */
  packages: tCreditPackage[] | null;
}): tConfirmSummary => {
  const {
    draft,
    selectedPackage,
    selectedPaymentMethod,
    unitPriceCents,
    workspaceCurrency,
    packages,
  } = params;
  const credits =
    selectedPackage?.credits ??
    (typeof draft.customCredits === 'number' ? draft.customCredits : 0);
  const unitFallback = unitPriceCents ?? 0;
  const totalCents = selectedPackage
    ? selectedPackage.price_cents
    : credits * unitFallback;
  const pricePerCreditCents =
    credits > 0
      ? Math.round(totalCents / credits)
      : unitFallback;
  const currency = resolveCreditCurrency({
    selectedPackage,
    workspaceCurrency,
    packages,
  });
  const expirationDateLabel = dayjs()
    .add(CREDITS_EXPIRATION_DAYS, 'day')
    .format('DD/MM/YYYY');
  return {
    credits,
    pricePerCreditCents,
    totalCents,
    currency,
    expirationDateLabel,
    paymentMethodLast4: selectedPaymentMethod?.last4 ?? null,
  };
};

/**
 * Format a price-cents value to the locale-friendly `${amount} ${currency}` shape
 * used across the confirm + success modals (e.g. `$990.00 MXN`).
 */
export const formatPriceWithCurrency = (
  cents: number,
  currency: string,
): string => `$${(cents / 100).toFixed(2)} ${currency}`;

/**
 * RET-1518 — credits-period instant formatting in the CLIENT'S timezone.
 *
 * Background: the renewal/expiration dates are a real UTC instant (the reset
 * cron floors `period_start` to UTC midnight — RET-1473). The old code parsed
 * the naive string as the browser's LOCAL time and showed only the date, so a
 * CST customer saw "renews the 23rd" while the reset actually fired at 18:00
 * the prior evening. With customers across MX/PE/CO/AR/ES the offset differs
 * per country, so we must convert the instant to each browser's own zone and
 * show the TIME, not a bare date.
 *
 * `parseCreditsInstant` is the single parse step that interprets the backend
 * string as UTC (see the `dayjs.extend(utc)` note above) and returns a real
 * `Date` instant. The formatters below render that instant via `Intl`, which
 * uses the browser's timezone — covering every country and its DST with no
 * hardcoded offset (contrast `date.functions.ts` `CDMX_OFFSET_HOURS`, which is
 * Mexico-only and would be wrong for the others).
 */
export const parseCreditsInstant = (value: string): Date | null => {
  if (!value) return null;
  const instant = dayjs.utc(value);
  return instant.isValid() ? instant.toDate() : null;
};

/**
 * Re-exported so this module's public surface and its tests stay unchanged. The
 * TODO that lived here — extract the shared map — is done: it now lives in
 * `src/functions/date.functions.ts`, where a third consumer could reach it
 * without importing this page's `dayjs` and WhatsappApi types.
 */
export { toIntlLocale };

/**
 * Format a credits-period instant as a localized date + time in the browser's
 * timezone, e.g. `22 jun 2026, 6:00 p. m.`. Returns `''` for empty/invalid
 * input so callers can keep their existing `value && (...)` guards. The zone
 * label (e.g. "hora local") is appended by the i18n sentence template, not
 * here, so it stays translatable.
 */
export const formatCreditsInstant = (
  value: string,
  locale: string,
): string => {
  const instant = parseCreditsInstant(value);
  if (!instant) return '';
  return new Intl.DateTimeFormat(toIntlLocale(locale), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(instant);
};

/**
 * Whole days between now and a credits-period instant, computed from the real
 * UTC instant (not the naive-as-local misread the old `dayjs(value).diff(...)`
 * produced, which could be off by a day at the timezone boundary). Returns 0
 * for empty/invalid input.
 */
export const creditsDaysRemaining = (value: string): number => {
  const instant = parseCreditsInstant(value);
  if (!instant) return 0;
  return dayjs(instant).diff(dayjs(), 'day');
};
